import React, { useCallback, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation, withTranslation } from 'react-i18next';
import { Box, Grid } from '@mui/material';
import { Button, IconButton, Typography } from '@totalsoft/rocket-ui';
import { useMutation, useReactiveVar } from '@apollo/client';
import { useToast, useActivityIndicatorContext } from 'utils/hooks';
import { SELECT_RCA_QUOTATION } from '../../../mutations';
import ErrorIcon from '@mui/icons-material/Error';
import { useServerErrorHandling } from 'utils/hooks/useServerErrorHandling';
import BookmarksIcon from '@mui/icons-material/Bookmarks';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { RightsContext } from 'providers/RightsContext';
import useInsuranceParams from '../../../useInsuranceParams';
import { rcaSelectedQuotationVar, rcaSelectionInProgressVar } from '../../../variables';
import { DirectSettlementAvatar, QuotationInsurerContainer, QuotationText, QuotationButton } from '../../InsuranceStyles';

const RCAQuotation = ({ quotation, isReadOnly }) => {
  const { t } = useTranslation();
  const { onBeginCall, onEndCall } = useActivityIndicatorContext();
  const addToast = useToast();

  const { isImage } = useContext(RightsContext);
  const { offerId, offerSiteId } = useInsuranceParams();
  const selectedQuotation = useReactiveVar(rcaSelectedQuotationVar);

  const [showErrorMessage, setShowErrorMessage] = useState(false);

  // #region Select Quotation

  const { storeKey } = useServerErrorHandling({
    effect: _ => {
      onEndCall();
    }
  });

  const [selectRcaQuotation] = useMutation(SELECT_RCA_QUOTATION, {
    context: {
      headers: { offerId, offerSiteId }
    },
    onError: err => {
      addToast(err.message, 'error');
      onEndCall();
    },
    onCompleted: data => {
      const { correlationId } = data.selectRcaQuotation;
      storeKey({ correlationId });
    }
  });

  const handleSelectQuotation = useCallback(
    quotationDetailId => {
      onBeginCall();
      rcaSelectionInProgressVar(true);
      selectRcaQuotation({
        variables: {
          insuranceSimulationId: quotation.insuranceSimulationId,
          insuranceQuotationId: quotation.id,
          quotationDetailId
        }
      });
    },
    [quotation, selectRcaQuotation, onBeginCall]
  );

  // #endregion

  const handleCheckSelectedQuotation = useCallback(
    detailId => {
      return selectedQuotation.id === quotation.id && quotation.rcaQuotation.selectedDetailId === detailId;
    },
    [quotation.id, quotation.rcaQuotation.selectedDetailId, selectedQuotation.id]
  );

  return (
    <Grid item container xs={12} spacing={1} alignItems='center'>
      <QuotationInsurerContainer item container xs={12} sm={5} xl={7}>
        {quotation.sourceInsuranceQuotationId ? <BookmarksIcon color='primary' /> : <BookmarkBorderIcon />}
        <QuotationText component={'span'} fontWeight={'fontWeightBold'} title={quotation.insurerName}>
          {quotation.insurerName}
        </QuotationText>
      </QuotationInsurerContainer>
      <Grid item container xs={12} sm={7} xl={4} spacing={1}>
        {quotation.rcaQuotation.details.length > 0
          ? quotation.rcaQuotation.details.map(rcaDetail => {
              const quotationIsSelected = handleCheckSelectedQuotation(rcaDetail.id);
              return (
                <Grid item xs={12} sm={9} md={5} key={rcaDetail.id}>
                  <QuotationButton
                    id={`RCA_Amount_${rcaDetail.directCompensationYN ? 'WithDD' : 'WithoutDD'}`}
                    fullWidth
                    tooltip={isImage ? '' : quotationIsSelected ? t('Selected') : t('Select')}
                    disabled={quotationIsSelected || isReadOnly() || isImage}
                    startIcon={rcaDetail.directCompensationYN && <DirectSettlementAvatar>{t('Offer.Insurance.DD')}</DirectSettlementAvatar>}
                    endIcon={quotationIsSelected && <CheckCircleIcon />}
                    onClick={() => handleSelectQuotation(rcaDetail.id)}
                    quotationIsSelected={quotationIsSelected}
                  >
                    {rcaDetail.insuranceAmount ? t('DECIMAL2', { number: rcaDetail.insuranceAmount }) + ' ' + quotation.currencyCode : '-'}
                  </QuotationButton>
                </Grid>
              );
            })
          : [1, 2].map(key => (
              <Grid item xs={12} md={5} key={key}>
                <Button fullWidth color='primary' disabled={true}>
                  {'-'}
                </Button>
              </Grid>
            ))}
        {quotation.errorMessage && (
          <Grid container item xs={12} sm={3} md={2} spacing={1} justifyContent='center' alignItems='center' wrap={'nowrap'}>
            <IconButton
              id={`insurerError`}
              size={'tiny'}
              variant='text'
              onClick={() => setShowErrorMessage(!showErrorMessage)}
              tooltip={quotation.errorMessage}
            >
              <ErrorIcon color={'error'} />
            </IconButton>
          </Grid>
        )}
      </Grid>
      {showErrorMessage && (
        <Grid item xs={12}>
          <Typography variant='caption' align='left'>
            <Box fontStyle='italic'>{quotation.errorMessage}</Box>
          </Typography>
        </Grid>
      )}
    </Grid>
  );
};

RCAQuotation.propTypes = {
  quotation: PropTypes.object.isRequired,
  isReadOnly: PropTypes.func.isRequired
};

export default withTranslation()(RCAQuotation);
